import type { User as FirebaseUser } from 'firebase/auth';
import { useCallback, useEffect, useRef, useState } from 'react';

const IDLE_TIMEOUT = 8 * 60 * 60 * 1000;
const ABSOLUTE_TIMEOUT = 24 * 60 * 60 * 1000;

interface UseIdleTimeoutProps {
  user: FirebaseUser | null;
  logoutUser: () => Promise<void>;
}

export const useIdleTimeout = ({ user, logoutUser }: UseIdleTimeoutProps) => {
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoggingOutInactivity, setIsLoggingOutInactivity] = useState<boolean>(false);
  const absoluteTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [loginTimestamp, setLoginTimestamp] = useState<number | null>(null);

  const logoutDueToInactivity = useCallback(async (): Promise<void> => {
    if (!user || isLoggingOutInactivity) {
      return;
    }

    setIsLoggingOutInactivity(true);

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    sessionStorage.setItem('loggedOutDueToInactivity', 'true');
    await logoutUser();
  }, [user, isLoggingOutInactivity, logoutUser]);

  const resetIdleTimer = useCallback((): void => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (user && !isLoggingOutInactivity) {
      const newTimer = setTimeout(logoutDueToInactivity, IDLE_TIMEOUT);
      idleTimerRef.current = newTimer;
    }
  }, [user, isLoggingOutInactivity, logoutDueToInactivity]);

  const handleUserActivity = useCallback((): void => {
    if (user) {
      resetIdleTimer();
    }
  }, [user, resetIdleTimer]);

  const logoutDueToAbsoluteTimeout = useCallback(async (): Promise<void> => {
    if (!user) {
      return;
    }
    if (absoluteTimerRef.current) {
      clearTimeout(absoluteTimerRef.current);
      absoluteTimerRef.current = null;
    }
    sessionStorage.setItem('loggedOutDueToAbsoluteTimeout', 'true');
    await logoutUser();
  }, [user, logoutUser]);

  useEffect(() => {
    if (user && loginTimestamp) {
      if (absoluteTimerRef.current) {
        clearTimeout(absoluteTimerRef.current);
      }
      const now = Date.now();
      const elapsed = now - loginTimestamp;
      const remaining = Math.max(ABSOLUTE_TIMEOUT - elapsed, 0);
      if (remaining > 0) {
        const timer = setTimeout(logoutDueToAbsoluteTimeout, remaining);
        absoluteTimerRef.current = timer;
      } else {
        logoutDueToAbsoluteTimeout();
      }
    } else {
      if (absoluteTimerRef.current) {
        clearTimeout(absoluteTimerRef.current);
        absoluteTimerRef.current = null;
      }
    }
    return () => {
      if (absoluteTimerRef.current) {
        clearTimeout(absoluteTimerRef.current);
        absoluteTimerRef.current = null;
      }
    };
  }, [user, loginTimestamp, logoutDueToAbsoluteTimeout]);

  useEffect(() => {
    if (user && !loginTimestamp) {
      setLoginTimestamp(Date.now());
    } else if (!user) {
      setLoginTimestamp(null);
    }
  }, [user, loginTimestamp]);

  useEffect(() => {
    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];

    if (user) {
      for (const event of activityEvents) {
        window.addEventListener(event, handleUserActivity);
      }
      resetIdleTimer();
    } else {
      for (const event of activityEvents) {
        window.removeEventListener(event, handleUserActivity);
      }
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      setIsLoggingOutInactivity(false);
    }

    return () => {
      for (const event of activityEvents) {
        window.removeEventListener(event, handleUserActivity);
      }
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [user, handleUserActivity, resetIdleTimer]);
};
